import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Send, Link2, Check, Zap,
} from 'lucide-react';
import { Button } from './ui/button';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * SupabasePublishSection — "send to website" control on a blog content piece
 * detail panel. Only appears for blog content, and only if the current project
 * has a connected Supabase (webhook) target. Mirrors WordPressPublishSection.
 */
export default function SupabasePublishSection({ contentPiece, getAuthHeader, onPublished, getContentHtml }) {
  const [status, setStatus] = useState({ loading: true, connected: false, connection: null });
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/integrations/supabase/status`, { headers: getAuthHeader() });
      setStatus({ loading: false, connected: !!data.connected, connection: data.connection || null });
    } catch (_e) {
      setStatus({ loading: false, connected: false, connection: null });
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  if (!contentPiece || contentPiece.platform !== 'blog') return null;

  const published = contentPiece.supabase_row_id || contentPiece.supabase_published_at;

  const handlePublish = async () => {
    setPublishing(true);
    try {
      // Send the frontend-rendered rich fragment (buildCleanHtml) — the single
      // source of truth. Fall back to any persisted fragment on the piece.
      // Backend sanitises it and, if absent, renders server-side.
      let contentHtml = '';
      try { contentHtml = (getContentHtml && getContentHtml()) || ''; } catch (_) { contentHtml = ''; }
      if (!contentHtml) contentHtml = contentPiece.content_html || '';
      const { data } = await axios.post(
        `${API}/integrations/supabase/publish/${contentPiece.id}`,
        contentHtml ? { content_html: contentHtml } : {},
        { headers: getAuthHeader(), timeout: 60000 }
      );
      toast.success('Auf Website veröffentlicht.');
      if (onPublished) onPublished({
        supabase_row_id: data?.supabase?.row_id ?? true,
        supabase_target: data?.supabase?.target,
        supabase_published_at: new Date().toISOString(),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Senden fehlgeschlagen.');
    } finally {
      setPublishing(false);
    }
  };

  if (status.loading) {
    return (
      <div className="border-t border-[#1A2B42] mt-5 pt-4 flex items-center gap-2 text-xs text-[#5A6C7D]" data-testid="supabase-publish-loading">
        <Loader2 className="w-3 h-3 animate-spin" />
        Website-Status wird geprüft…
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="border-t border-[#1A2B42] mt-5 pt-4" data-testid="supabase-publish-not-connected">
        <div className="flex items-start gap-2 text-xs bg-amber-500/5 border border-amber-500/20 text-amber-300/80 rounded px-3 py-2">
          <Link2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Website (Supabase) ist für dieses Projekt nicht verbunden. Verbinde sie unter{' '}
            <a href="/app/settings?tab=integrations" className="underline text-[#00D9C8]">Einstellungen → Integrationen</a>.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-[#1A2B42] mt-5 pt-4 space-y-3" data-testid="supabase-publish-section">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-white flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#3ECF8E]" fill="currentColor" aria-hidden="true" />
          An Website senden
        </h4>
        <span className="text-[10px] text-[#5A6C7D] break-all">
          {(status.connection?.webhook_url || '').replace(/^https?:\/\//, '')}
        </span>
      </div>

      {published && (
        <div className="flex items-center gap-2 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded px-3 py-2" data-testid="supabase-already-published">
          <Check className="w-3.5 h-3.5 shrink-0" />
          <span>Bereits an die Website gesendet.</span>
        </div>
      )}

      <Button
        onClick={handlePublish}
        disabled={publishing}
        className="w-full bg-[#3ECF8E] hover:bg-[#34b87c] text-[#0A1628] font-medium"
        data-testid="supabase-publish-execute-btn"
      >
        {publishing ? (
          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Wird gesendet…</>
        ) : (
          <><Send className="w-4 h-4 mr-1" /> {published ? 'Erneut senden' : 'An Website senden'}</>
        )}
      </Button>

      <p className="text-[10px] text-[#5A6C7D] leading-relaxed">
        Der Beitrag wird mit Titel, Meta-Beschreibung, Beitragsbild und dem generierten HTML gesendet.
        Bei gleichem Slug aktualisiert die Website den bestehenden Beitrag.
      </p>
    </div>
  );
}
